import { useEffect, useState, useCallback } from 'react';
import adminFetch from '../../services/adminFetch';
import './StreamAdminPage.css';
import './InviteCodePage.css';

const STATUS_LABEL = {
  unused:  { text: '未使用', cls: 'invite-status-unused' },
  used:    { text: '已使用', cls: 'invite-status-used' },
  expired: { text: '已过期', cls: 'invite-status-expired' },
  revoked: { text: '已撤销', cls: 'invite-status-revoked' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('zh-CN', { hour12: false });
}

export default function InviteCodePage() {
  // 列表
  const [codes, setCodes]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [listErr, setListErr] = useState('');

  // 生成表单
  const [count, setCount]           = useState(5);
  const [expiresDays, setExpiresDays] = useState(30);
  const [generating, setGenerating] = useState(false);
  const [genErr, setGenErr]         = useState('');
  const [newCodes, setNewCodes]     = useState([]);

  // 撤销
  const [revoking, setRevoking] = useState('');

  const loadCodes = useCallback(async () => {
    setLoading(true);
    setListErr('');
    try {
      const res = await adminFetch('/admin/invites');
      if (!res.ok) throw new Error(`加载失败 (${res.status})`);
      const data = await res.json();
      setCodes(Array.isArray(data) ? data : []);
    } catch (e) {
      setListErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCodes(); }, [loadCodes]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    setGenErr('');
    setNewCodes([]);
    try {
      const res = await adminFetch('/admin/invites/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: Number(count), expires_days: Number(expiresDays) }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `生成失败 (${res.status})`);
      }
      const data = await res.json();
      setNewCodes(data.codes || []);
      await loadCodes();
    } catch (e) {
      setGenErr(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (code) => {
    if (!window.confirm(`确认撤销邀请码 ${code}？`)) return;
    setRevoking(code);
    try {
      const res = await adminFetch(`/admin/invites/${code}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || '撤销失败');
        return;
      }
      await loadCodes();
    } catch (e) {
      alert(e.message);
    } finally {
      setRevoking('');
    }
  };

  const copyAll = () => {
    navigator.clipboard.writeText(newCodes.join('\n'))
      .then(() => alert('已复制到剪贴板'))
      .catch(() => alert('复制失败，请手动复制'));
  };

  return (
    <div className="admin-page">
      <header className="admin-header">
        <h1>邀请码管理</h1>
        <button type="button" className="admin-refresh-btn" onClick={loadCodes}>刷新</button>
      </header>

      <div className="admin-body">

        {/* 生成区 */}
        <div className="admin-section">
          <h2 className="section-title">生成邀请码</h2>
          <form className="invite-gen-form" onSubmit={handleGenerate}>
            <label className="invite-gen-field">
              <span>数量</span>
              <input
                type="number" min="1" max="50" value={count}
                onChange={e => setCount(e.target.value)}
              />
            </label>
            <label className="invite-gen-field">
              <span>有效天数</span>
              <input
                type="number" min="1" max="365" value={expiresDays}
                onChange={e => setExpiresDays(e.target.value)}
              />
            </label>
            <button type="submit" className="btn-primary invite-gen-btn" disabled={generating}>
              {generating ? '生成中…' : '生成'}
            </button>
          </form>
          {genErr && <p className="invite-error">{genErr}</p>}

          {newCodes.length > 0 && (
            <div className="invite-new-codes">
              <div className="invite-new-codes-header">
                <span>新生成 {newCodes.length} 个邀请码</span>
                <button type="button" className="admin-refresh-btn" onClick={copyAll}>复制全部</button>
              </div>
              <div className="invite-code-grid">
                {newCodes.map(c => (
                  <span key={c} className="invite-code-chip">{c}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 列表区 */}
        <div className="admin-section">
          <h2 className="section-title">
            邀请码列表
            <span className="hint" style={{ marginLeft: 8 }}>共 {codes.length} 条</span>
          </h2>

          {listErr && <p className="invite-error">{listErr}</p>}

          {loading ? (
            <p style={{ color: '#888' }}>加载中…</p>
          ) : codes.length === 0 ? (
            <p style={{ color: '#888' }}>暂无邀请码</p>
          ) : (
            <div className="invite-table-wrap">
              <table className="admin-stream-list-table invite-table">
                <thead>
                  <tr>
                    <th>邀请码</th>
                    <th>状态</th>
                    <th>使用者</th>
                    <th>使用时间</th>
                    <th>过期时间</th>
                    <th>创建时间</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {codes.map(item => {
                    const s = STATUS_LABEL[item.status] || { text: item.status, cls: '' };
                    return (
                      <tr key={item.code}>
                        <td className="invite-code-cell">{item.code}</td>
                        <td><span className={`invite-status-badge ${s.cls}`}>{s.text}</span></td>
                        <td>{item.used_by_username || '—'}</td>
                        <td>{fmtDate(item.used_at)}</td>
                        <td>{fmtDate(item.expires_at)}</td>
                        <td>{fmtDate(item.created_at)}</td>
                        <td>
                          {item.status === 'unused' && (
                            <button
                              type="button"
                              className="btn-sm btn-danger"
                              disabled={revoking === item.code}
                              onClick={() => handleRevoke(item.code)}
                            >
                              {revoking === item.code ? '…' : '撤销'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
